import {
  ShieldCheckIcon,
  LockClosedIcon,
  EyeSlashIcon,
  ServerIcon,
} from '@heroicons/react/24/outline';

const securityFeatures = [
  {
    name: 'Read-Only Access',
    description: 'We only read security posture data. No enforcement actions, no modifications to your environment.',
    icon: EyeSlashIcon,
  },
  {
    name: 'Encrypted Storage',
    description: 'All OAuth tokens and sensitive data are encrypted using AES-256-GCM encryption at rest.',
    icon: LockClosedIcon,
  },
  {
    name: 'Tenant Isolation',
    description: 'Each organization has fully isolated data. Multi-tenant architecture with strict data boundaries.',
    icon: ServerIcon,
  },
  {
    name: 'Official APIs Only',
    description: 'We use official Microsoft Graph API and Google Admin SDK with proper scopes and permissions.',
    icon: ShieldCheckIcon,
  },
];

export default function AboutPage() {
  return (
    <div className="bg-white dark:bg-slate-900">
      {/* Hero */}
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-5xl">
            About CyberActions
          </h1>
          <p className="mt-6 text-lg text-slate-600 dark:text-slate-300">
            CyberActions is the Cloud Security Posture Auditor for Microsoft 365
            and Google Workspace, designed for UK businesses and MSPs.
          </p>
        </div>
      </div>

      {/* Mission */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Our Mission</h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              We believe that understanding your security posture shouldn't require complex tools or
              expensive consultants. CyberActions provides clear, actionable insights into your cloud security
              configuration, helping you meet compliance requirements like Cyber Essentials, GDPR, and
              ISO 27001.
            </p>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              Our read-only approach means you can audit your environment safely, without any risk of
              unintended changes. We focus on visibility and reporting, leaving enforcement decisions
              to you.
            </p>
          </div>
        </div>
      </div>

      {/* Security Section */}
      <div id="security" className="py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
              Security First
            </h2>
            <p className="mt-6 text-lg text-slate-600 dark:text-slate-300">
              We take security seriously. Here's how we protect your data.
            </p>
          </div>

          <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 lg:max-w-none lg:grid-cols-2">
            {securityFeatures.map((feature) => (
              <div
                key={feature.name}
                className="flex gap-4 rounded-xl bg-white dark:bg-slate-800 p-6 shadow-sm border border-slate-200 dark:border-slate-700"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30 flex-shrink-0">
                  <feature.icon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">{feature.name}</h3>
                  <p className="mt-2 text-slate-600 dark:text-slate-300">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* What We Check */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
              What We Audit
            </h2>
          </div>

          <div className="mx-auto mt-16 grid max-w-lg grid-cols-1 gap-8 lg:max-w-none lg:grid-cols-2">
            {/* Microsoft 365 */}
            <div className="rounded-xl bg-white dark:bg-slate-800 p-8 shadow-sm border border-slate-200 dark:border-slate-700">
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Microsoft 365</h3>
              <ul className="mt-6 space-y-3">
                {[
                  'MFA status and authentication methods',
                  'Conditional access policy summary',
                  'User risk and sign-in risk levels',
                  'Device compliance status (Intune)',
                  'Admin role assignments',
                  'External sharing settings',
                  'Mailbox forwarding rules',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <ShieldCheckIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600 dark:text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Google Workspace */}
            <div className="rounded-xl bg-white dark:bg-slate-800 p-8 shadow-sm border border-slate-200 dark:border-slate-700">
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Google Workspace</h3>
              <ul className="mt-6 space-y-3">
                {[
                  '2-step verification status',
                  'User list and privileges',
                  'Mobile and desktop device inventory',
                  'Drive file sharing settings',
                  'External sharing configuration',
                  'Admin role assignments',
                  'Email routing and forwarding',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <ShieldCheckIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600 dark:text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Contact */}
      <div id="contact" className="py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
              Get in Touch
            </h2>
            <p className="mt-6 text-lg text-slate-600 dark:text-slate-300">
              Have questions? We'd love to hear from you.
            </p>
            <p className="mt-4 text-primary-600 dark:text-primary-400 font-medium">
              support@cyberactions.co.uk
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
